import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { FunctionComponent } from 'react';
import clsx from 'clsx';
import {
  BillInfo,
  AutoPayInfo,
  ViewBillDetails,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import { toSentenceCase } from '../../../util/format';
import CurrentBillCardBillSummary from '../current-bill-card-bill-summary/CurrentBillCardBillSummary';
import { CardContent, Paper } from '@material-ui/core';
import ErrorOverlay from '../../error/error-overlay';
import { TpaAccountDetails } from '../../need-more-time-to-pay/tpa/useTPA';
import { EnergySavingsTips } from '../current-bill/EnergySavingTips';
import CurrentBillPaymentSummary from '../current-bill-payment-summary';
import { useIsMobile } from '../../../util/style-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      display: 'block',
      backgroundColor: colors.lightBlue1,
      marginTop: '3px',
      padding: '13px 24px',
      borderRadius: '3px 3px 0px 0px',
    },
    titleText: {
      color: colors.noirBlur,
      fontSize: '20px',
      fontWeight: 'normal',
    },
    container: {
      padding: theme.spacing(2),
    },
    cardLayout: {
      width: '100%',
      borderRadius: '4px',
      boxShadow: `inset 0px 2px 0px ${colors.sparkBlue}, 0px 3px 6px #00000029`,
    },

    autopayPaperCard: {
      boxShadow: 'inset 0px 2px 0px #27788C, 0px 3px 6px #00000029',
    },
    noBillPaperCard: {
      boxShadow: 'inset 0px 2px 0px #1C304A, 0px 3px 6px #00000029',
    },
    pastDuePaperCard: {
      boxShadow: 'inset 0px 2px 0px red, 0px 3px 6px #00000029',
    },
    boldText: {
      fontWeight: 'bold',
    },
    billContainer: {
      margin: '0.5rem',
      display: 'flex',
      flexDirection: 'row',
      background: colors.riverBlue,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      '& .MuiPaper-elevation1': {
        boxShadow: 'none',
      },
    },
    billDetailsContainer: {
      margin: '1rem 0rem 1rem 1rem',
      background: colors.white,
      padding: '20px 12px',
      [theme.breakpoints.down('sm')]: {
        margin: '0.5rem',
      },
    },
  }),
);

interface CurrentBillProps {
  totalPendingCharges?: number | null;
  billInfo: BillInfo | null;
  autoPay: AutoPayInfo | null;
  isPendingDisconnect: boolean | undefined;
  billDetails: ViewBillDetails | null;
  isSummaryBillAccount: boolean | undefined;
  tpaDetails: TpaAccountDetails | undefined;
  isNewAccount: boolean | null | undefined;
}

const BillSummary: FunctionComponent<CurrentBillProps> = ({
  totalPendingCharges,
  billInfo,
  autoPay,
  isPendingDisconnect,
  billDetails,
  isSummaryBillAccount,
  tpaDetails,
  isNewAccount,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const papercardStyle = clsx({
    [classes.cardLayout]: true, //always applies
    [classes.noBillPaperCard]: !Boolean(billDetails),
    [classes.pastDuePaperCard]: isPendingDisconnect,
    [classes.autopayPaperCard]: Boolean(autoPay?.isEnrolled),
  });

  return (
    <Paper className={papercardStyle}>
      <CardContent style={{ padding: '0' }}>
        {billInfo && autoPay ? (
          <>
            <Grid container direction="row">
              <Grid item container direction="row" className={classes.title}>
                <Grid item xs={12}>
                  <Typography className={classes.titleText} variant="h4">
                    {toSentenceCase(t('CURRENT_BILL'))}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container direction="row">
                <CurrentBillPaymentSummary
                  totalPendingCharges={totalPendingCharges}
                  billInfo={billInfo}
                  autoPay={autoPay}
                  amountCreditDisplay={'CR'}
                  isMobile={isMobile}
                  viewBillDetails={billDetails}
                  isSummaryBillAccount={isSummaryBillAccount}
                  isPendingDisconnect={isPendingDisconnect}
                  isNewAccount={isNewAccount}
                />

                <Grid className={classes.billContainer}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.billDetailsContainer}
                  >
                    <Grid item>
                      <Typography
                        variant="body1"
                        className={classes.boldText}
                        data-testid="bill-details"
                      >
                        {t('BILL_DETAILS')}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} style={{ margin: '0px 0.5rem' }}>
                      <CurrentBillCardBillSummary
                        viewBillDetails={billDetails}
                        isMobile={isMobile}
                        tpaDetails={tpaDetails}
                        isNewAccount={isNewAccount}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EnergySavingsTips />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <ErrorOverlay title={t('CURRENT_BILL')} />
        )}
      </CardContent>
    </Paper>
  );
};

export default BillSummary;
